"use client";

import { type VariantProps, cva } from "class-variance-authority";
import type { ElementType, JSX, JSXElementConstructor } from "react";
import { Button as AriaButton } from "react-aria-components";

import { useHandlersWithStatus } from "@/components/status";
import { Color } from "@whitecircle/graphql/graphql";

const variants = cva(
	"cursor-pointer disabled:cursor-auto group inline-flex overflow-hidden transition-[opacity,background-color,box-shadow] whitespace-nowrap items-center justify-center text-14/22 shrink-0 disabled:opacity-75",
	{
		variants: {
			variant: {
				default:
					"bg-primary text-primary-foreground not-disabled:hover:bg-primary/90",
				outline:
					"bg-background border inset-shadow-glow/20 not-disabled:hover:inset-shadow-glow/25",
				link: "",
			},
			size: {
				default: "rounded-full font-[570] px-4 py-2 h-9",
				large: "rounded-full pl-3 pr-5 py-2",
				small: "rounded-2 h-7 px-3",
				round: "rounded-full",
				"round-small": "rounded-full h-7 px-3",
				link: "",
			},
			color: {
				[Color.Blue]: "border-blue/20 text-blue inset-shadow-blue",
				[Color.Pink]: "border-pink/20 text-pink inset-shadow-pink",
				[Color.Purple]: "border-purple/20 text-purple inset-shadow-purple",
				[Color.Gray]: "border-white/20 text-grey inset-shadow-white",
				[Color.Yellow]: "border-yellow/20 text-yellow inset-shadow-yellow",
				[Color.Red]: "border-red/20 text-red inset-shadow-red",
				[Color.Orange]: "border-orange/20 text-orange inset-shadow-orange",
				[Color.Green]: "border-green/20 text-green inset-shadow-green",
				[Color.Cyan]: "border-cyan/20 text-cyan inset-shadow-cyan",
				[Color.White]: "border-white/20 text-white inset-shadow-white",
			},
		},
		defaultVariants: {
			variant: "default",
			size: "default",
		},
	},
);

type Props<T extends ElementType> = (T extends JSXElementConstructor<infer P>
	? P
	: T extends keyof JSX.IntrinsicElements
		? JSX.IntrinsicElements[T]
		: never) &
	VariantProps<typeof variants> & {
		as?: T;
		className?: string;
	};

export function Button<T extends ElementType = typeof AriaButton>({
	className,
	color,
	size,
	variant,
	as,
	children,
	...props
}: Props<T>) {
	const handlers = useHandlersWithStatus({ onPress: props.onPress });
	const Component = as ?? AriaButton;

	return (
		<Component
			{...props}
			{...handlers}
			className={variants({ color, size, variant, className })}
		>
			<span className="group-data-pending:hidden contents">{children}</span>
		</Component>
	);
}
